import React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiAdoptable } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import CheckIcon from '@wix/da-ds/pkg/Icons/16x16/Check';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import Avatar from '../../../User/Avatar';
import UserLink from '../../../User/Link/UserLink';

import s from './AdoptableOwner.scss';

interface Props {
  adoptable: PapiAdoptable;
  className?: string;
}

const AdoptableOwner: React.FC<Props> = ({ adoptable, className }) => {
  const { t } = useTranslation();

  // We shouldn't show this component if the author and the owner are the same user. Except when it's been sold before.
  if (adoptable.owner.userId === adoptable.creator.userId && adoptable.saleCount === 0) {
    return null;
  }

  const content = (
    <div className={cn(s['root'], className)}>
      <Avatar user={adoptable.owner} size={24} linkTo="exclusives" />
      <div>
        {t('deviation.details.adopted')}{' '}
        <UserLink
          className={s['owner-userlink']}
          user={adoptable.owner}
          anchor
          linkTo="exclusives"
        />
      </div>
    </div>
  );

  if (adoptable.isCreator || adoptable.isOwner) {
    const tooltipText = (
      <>
        {t(
          adoptable.isCreator
            ? 'deviation.details.adopted.tooltip.creator'
            : 'deviation.details.adopted.tooltip.owner'
        )}
        {adoptable.isOwner && adoptable.deviationId && (
          <PlainButtonOrLink
            className={s['download-link']}
            href={Url.adoptableSourceFileLink(adoptable.deviationId)}
          >
            {t('deviation.details.adopted.tooltip.downloadLink')}
          </PlainButtonOrLink>
        )}
      </>
    );
    return (
      <SimpleTooltip
        text={tooltipText}
        prefix={<CheckIcon size={IconSize.SMALL} />}
        contentClassName={s['tooltip']}
      >
        {content}
      </SimpleTooltip>
    );
  }

  return content;
};
AdoptableOwner.displayName = 'AdoptableOwner';

export default AdoptableOwner;
